
import React, { useState } from 'react';
import { Interview } from '@/types/interview';
import { Table, TableHeader, TableRow, TableHead, TableBody } from '@/components/ui/table';
import ViewInterviewDialog from './ViewInterviewDialog';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmationDialog from './ConfirmationDialog';
import PublishedInterviewRow from './published/PublishedInterviewRow';
import EmptyPublishedState from './published/EmptyPublishedState';
import { useDepublishInterview } from '@/hooks/interview/useDepublishInterview';
import { useDeleteInterview } from '@/hooks/interview/useDeleteInterview';

interface PublishedInterviewsListProps {
  interviews: Interview[];
}

const PublishedInterviewsList: React.FC<PublishedInterviewsListProps> = ({ interviews }) => {
  const queryClient = useQueryClient();
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  
  // Added dummy states to satisfy ViewInterviewDialog props
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [sendingToFeederId, setSendingToFeederId] = useState<string | null>(null);
  
  const {
    processingId: depublishProcessingId,
    isConfirmDialogOpen: isDepublishDialogOpen,
    interviewToDepublish,
    confirmDepublish,
    closeConfirmDialog: closeDepublishDialog,
    confirmAndDepublish
  } = useDepublishInterview();

  const {
    processingId: deleteProcessingId,
    isConfirmDialogOpen: isDeleteDialogOpen,
    interviewToDelete,
    confirmDelete,
    closeConfirmDialog: closeDeleteDialog,
    confirmAndDelete
  } = useDeleteInterview();

  const isProcessing = !!depublishProcessingId || !!deleteProcessingId;

  const handleViewInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedInterview(null);
  };

  // Dummy handlers for ViewInterviewDialog props
  const handlePublish = async (interviewId: string): Promise<void> => {
    // This function won't be used in the published interviews context
    // but is required by the ViewInterviewDialog props
    console.log('Publisering forespurt men ignorert i PublishedInterviewsList');
    return Promise.resolve();
  };

  const handleSendToFeeder = async (interviewId: string): Promise<void> => {
    // This function won't be used in the published interviews context
    // but is required by the ViewInterviewDialog props
    console.log('Send til køen forespurt men ignorert i PublishedInterviewsList');
    return Promise.resolve();
  };

  if (interviews.length === 0) {
    return <EmptyPublishedState />;
  }

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">Publiserte intervjuer ({interviews.length})</h3>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Intervjuobjekt</TableHead>
              <TableHead>Tilknytning</TableHead>
              <TableHead>Publiseringsdato</TableHead>
              <TableHead>Publiseringstid</TableHead>
              <TableHead>Handlinger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {interviews.map((interview) => (
              <PublishedInterviewRow
                key={interview.id}
                interview={interview}
                onViewInterview={handleViewInterview}
                onDepublish={confirmDepublish}
                onDelete={confirmDelete}
                isProcessing={isProcessing}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      <ViewInterviewDialog 
        interview={selectedInterview}
        isOpen={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        onPublish={handlePublish}
        onSendToFeeder={handleSendToFeeder}
        publishingId={publishingId}
        sendingToFeederId={sendingToFeederId}
      />

      <ConfirmationDialog
        isOpen={isDepublishDialogOpen}
        onClose={closeDepublishDialog}
        onConfirm={confirmAndDepublish}
        title="Flytt intervju til ventende"
        description="Er du sikker på at du vil flytte dette intervjuet tilbake til ventende-listen? Dette vil fjerne intervjuet fra hovedsiden."
        confirmText="Bekreft"
        cancelText="Kanseller"
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmAndDelete}
        title="Slett artikkel"
        description="Er du sikker på at du vil slette denne artikkelen permanent? Dette kan ikke angres."
        confirmText="Slett"
        cancelText="Kanseller"
        isDanger={true}
      />
    </div>
  );
};

export default PublishedInterviewsList;
